import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance with default config
const qaAPI = axios.create({
  baseURL: `${API_BASE_URL}/qa`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth interceptor
qaAPI.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * QA Dashboard API
 */
export const getQADashboard = async (filters = {}) => {
  try {
    const params = new URLSearchParams(filters);
    const response = await qaAPI.get(`/dashboard?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching QA dashboard:', error);
    throw error;
  }
};

/**
 * Get calls available for QA review
 */
export const getCallsForReview = async (filters = {}) => {
  try {
    const params = new URLSearchParams({
      page: 1,
      limit: 20,
      ...filters
    });
    const response = await qaAPI.get(`/calls/review?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching calls for review:', error);
    throw error;
  }
};

/**
 * Get call details for QA analysis
 */
export const getCallForAnalysis = async (callId) => {
  try {
    const response = await qaAPI.get(`/calls/${callId}/analysis`);
    return response.data;
  } catch (error) {
    console.error('Error fetching call for analysis:', error);
    throw error;
  }
};

/**
 * Submit manual QA evaluation
 */
export const submitQAEvaluation = async (callId, evaluation) => {
  try {
    const response = await qaAPI.post(`/calls/${callId}/evaluate`, evaluation);
    return response.data;
  } catch (error) {
    console.error('Error submitting QA evaluation:', error);
    throw error;
  }
};

/**
 * Generate AI-based QA evaluation
 */
export const generateAIEvaluation = async (callId) => {
  try {
    const response = await qaAPI.post(`/calls/${callId}/ai-evaluate`);
    return response.data;
  } catch (error) {
    console.error('Error generating AI evaluation:', error);
    throw error;
  }
};

/**
 * Get QA score audit trail for a call
 */
export const getQAScoreAuditTrail = async (callId) => {
  try {
    const response = await qaAPI.get(`/calls/${callId}/audit-trail`);
    return response.data;
  } catch (error) {
    console.error('Error fetching QA score audit trail:', error);
    throw error;
  }
};

/**
 * Get QA metrics and analytics
 */
export const getQAMetrics = async (filters = {}) => {
  try {
    const params = new URLSearchParams(filters);
    const response = await qaAPI.get(`/metrics?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching QA metrics:', error);
    throw error;
  }
};

/**
 * Stream call audio
 */
export const getCallAudioURL = (callId) => {
  const token = localStorage.getItem('token');
  return `${API_BASE_URL}/qa/calls/${callId}/audio?token=${token}`;
};

/**
 * Get calls for comparison
 */
export const getCallsForComparison = async (callIds) => {
  try {
    const response = await qaAPI.post('/calls/comparison', { callIds });
    return response.data;
  } catch (error) {
    console.error('Error fetching calls for comparison:', error);
    throw error;
  }
};

/**
 * Submit comparative analysis
 */
export const submitComparativeAnalysis = async (analysisData) => {
  try {
    const response = await qaAPI.post('/calls/comparative-analysis', analysisData);
    return response.data;
  } catch (error) {
    console.error('Error submitting comparative analysis:', error);
    throw error;
  }
};

/**
 * Export QA report
 */
export const exportQAReport = async (filters = {}, format = 'excel') => {
  try {
    const params = new URLSearchParams({ ...filters, format });
    const response = await qaAPI.get(`/reports/export?${params}`, {
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `qa-report-${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    console.error('Error exporting QA report:', error);
    throw error;
  }
};

/**
 * Bulk evaluate calls
 */
export const bulkEvaluateCalls = async (callIds, evaluationTemplate) => {
  try {
    const response = await qaAPI.post('/calls/bulk-evaluate', {
      callIds,
      evaluationTemplate
    });
    return response.data;
  } catch (error) {
    console.error('Error performing bulk evaluation:', error);
    throw error;
  }
};

/**
 * Get QA evaluation criteria
 */
export const getQACriteria = async () => {
  try {
    const response = await qaAPI.get('/criteria');
    return response.data;
  } catch (error) {
    console.error('Error fetching QA criteria:', error);
    throw error;
  }
};

/**
 * Save QA evaluation template
 */
export const saveEvaluationTemplate = async (template) => {
  try {
    const response = await qaAPI.post('/templates', template);
    return response.data;
  } catch (error) {
    console.error('Error saving evaluation template:', error);
    throw error;
  }
};

/**
 * Get evaluation templates
 */
export const getEvaluationTemplates = async () => {
  try {
    const response = await qaAPI.get('/templates');
    return response.data;
  } catch (error) {
    console.error('Error fetching evaluation templates:', error);
    throw error;
  }
};

/**
 * Get leads associated with a call
 */
export const getLeadsByCallId = async (callId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/leads/by-call/${callId}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching leads by call ID:', error);
    throw error;
  }
};

/**
 * Update lead information
 */
export const updateLead = async (leadId, leadData) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/leads/${leadId}`, leadData, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating lead:', error);
    throw error;
  }
};

/**
 * Update lead disposition
 */
export const updateLeadDisposition = async (leadId, disposition, notes = '') => {
  try {
    const response = await axios.put(`${API_BASE_URL}/leads/${leadId}/disposition`, {
      disposition,
      notes
    }, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating lead disposition:', error);
    throw error;
  }
};

/**
 * Get field mappings
 */
export const getFieldMappings = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/leads/field-mappings`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching field mappings:', error);
    throw error;
  }
};

export default {
  getQADashboard,
  getCallsForReview,
  getCallForAnalysis,
  submitQAEvaluation,
  generateAIEvaluation,
  getQAScoreAuditTrail,
  getQAMetrics,
  getCallAudioURL,
  getCallsForComparison,
  submitComparativeAnalysis,
  exportQAReport,
  bulkEvaluateCalls,
  getQACriteria,
  saveEvaluationTemplate,
  getEvaluationTemplates,
  getLeadsByCallId,
  updateLead,
  updateLeadDisposition,
  getFieldMappings
}; 